import { GlassPanel } from "@/components/ui/glass-panel";
import { Flame, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { getShowerStatuses } from "@/lib/astronomy";

function fmtDate(d: Date): string {
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function fmtRel(days: number): string {
  if (Math.abs(days) < 1) return "Today";
  if (days > 0 && days < 1.5) return "Tomorrow";
  if (days < 0 && days > -1.5) return "Yesterday";
  if (days > 0) return `in ${Math.round(days)} days`;
  return `${Math.round(-days)} days ago`;
}

function rateZhr(zhr: number): { tone: string; label: string } {
  if (zhr >= 80) return { tone: "text-rose-400 border-rose-500/30 bg-rose-500/10", label: "Major" };
  if (zhr >= 30) return { tone: "text-orange-400 border-orange-500/30 bg-orange-500/10", label: "Strong" };
  if (zhr >= 15) return { tone: "text-amber-400 border-amber-500/30 bg-amber-500/10", label: "Moderate" };
  return { tone: "text-slate-300 border-slate-500/30 bg-slate-500/10", label: "Minor" };
}

export function MeteorShowers() {
  const today = new Date();
  const all = getShowerStatuses(today);
  const active = all.filter((s) => s.active);
  const upcoming = all.filter((s) => !s.active && s.daysToPeak >= 0).slice(0, 3);

  return (
    <GlassPanel intensity="low" className="p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/10 ring-1 ring-rose-500/20">
          <Flame className="h-4 w-4 text-rose-400" />
        </div>
        <div>
          <h3 className="text-sm font-bold tracking-tight">Meteor Showers</h3>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Active now and approaching
          </p>
        </div>
      </div>

      {active.length === 0 ? (
        <div className="rounded-lg border border-border/40 bg-background/30 px-3 py-3 text-center mb-3">
          <p className="text-xs text-muted-foreground">
            No major showers active right now. Next event below.
          </p>
        </div>
      ) : (
        <div className="space-y-2 mb-4">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
            Active now
          </p>
          {active.map((s) => {
            const r = rateZhr(s.zhr);
            return (
              <div
                key={s.name}
                className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 sm:p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h4 className="text-sm font-bold">{s.name}</h4>
                    <p className="text-[11px] text-muted-foreground">
                      Parent: {s.parent}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "rounded-full border text-[10px] font-bold uppercase tracking-wider px-2 py-0.5",
                      r.tone,
                    )}
                  >
                    {r.label} · ZHR {s.zhr}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <p className="text-muted-foreground">Active</p>
                    <p className="font-semibold tabular-nums">
                      {s.startMonth}/{s.startDay} – {s.endMonth}/{s.endDay}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Peak</p>
                    <p className="font-semibold tabular-nums">
                      {fmtDate(s.peakDate)} · {fmtRel(s.daysToPeak)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Velocity</p>
                    <p className="font-semibold tabular-nums">{s.velocityKms} km/s</p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{s.notes}</p>
              </div>
            );
          })}
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            Upcoming
          </p>
          <div className="space-y-1.5">
            {upcoming.map((s) => {
              const r = rateZhr(s.zhr);
              return (
                <div
                  key={s.name}
                  className="rounded-lg border border-border/40 bg-background/30 px-3 py-2 flex items-center gap-3"
                >
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">{s.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      Peak {fmtDate(s.peakDate)} · {fmtRel(s.daysToPeak)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "rounded-full border text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 flex-shrink-0",
                      r.tone,
                    )}
                  >
                    ZHR {s.zhr}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </GlassPanel>
  );
}
